/**
 * Disabling the danger rule in this file cause no harm cause the html
 * that we're working on is sanitized
 *
 * For more information see:
 * https://github.com/yannickcr/eslint-plugin-react/blob/master/docs/rules/no-danger.md
 */
/* eslint-disable react/no-danger */
import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import parse from 'html-react-parser';
import images from '_images';

import { cts_secure } from '_cts';
import apis from '_apis';
import { colors } from '_styles';
import { Drawer } from '_components';
import { isRTL, eventTracker } from '_helpers';

import Title from './components';
import Loader from './Loader';

/**
 * import styles
 */
const parentWidth = document.getElementById('rcroot').getBoundingClientRect().width;

const Container = styled.div`
  overflow: hidden;
  margin: 0 auto;
  background-color: #fff;
  width: 100%;
  height: auto;
  padding: 0;
  text-align: left !important;
  &.content-container-responsive {
    width: calc(100% - 135px);
    padding: 0;
    margin: 0 auto;
  }
`;

const ContentContainer = styled.div`
  line-height: 1.8em !important;

  p {
    font-size: 15px !important;
    font-weight: 500 !important;
    display: block;
    line-height: 1.8em !important;
    color: #666;
    text-indent: 0;

    strong, b {
      font-weight: bold !important;
    }

    a {
      all: initial;
  font-family: Roboto, sans-serif !important;
      display: inline;
       
      font-weight: bold !important;
      font-size: 16px !important;
      cursor: pointer;
      border-bottom: 1px solid transparent !important;
      padding-bottom: 3px !important;

      color: ${colors.RC_ACTION} !important;
      text-decoration: none !important;

      &:hover {
        color: ${colors.RC_ACTION} !important;
        text-decoration: none !important;
        border-bottom: 1px solid #11A9CE !important;
        padding-bottom: 3px !important;
      }
  
    }
  }

  section, div {
    margin-bottom: 15px;
    display: block;
    border-width: 2px;

    ul {
      list-style: disc;
      margin-top: 15px;
      margin-bottom: 14.14286px ;
      line-height: 2em !important;
      padding-left: 15px;
      box-sizing: border-box;

      li {
        all: initial;
  font-family: Roboto, sans-serif !important;
        list-style: disc;
        font-weight: 500 !important;
        color: #666 !important;
        display: block;
         
        font-size: 16px !important;
        line-height: 1.5 !important;
        background-position: 15.42857px 7.07143px;
        background-size: 10px auto;
        padding: 0 0 7.71429px 38.57143px;
        background-image: url(${images.icons.bullet});
        background-repeat: no-repeat;
        box-sizing: border-box;
      }
    }
  }

  a {
    all: initial;
  font-family: Roboto, sans-serif !important;
    border: none;
    text-align: center;
    cursor: pointer;
    display: initial;
    margin: 10px auto;
    background-color: transparent;
    font-weight: bold !important;
    font-size: 16px !important;
    border-bottom: 1px solid transparent !important;
    padding-bottom: 3px !important;

    &::-moz-focus-inner {
      border: 0;
    }

    color: ${colors.RC_ACTION} !important;
    text-decoration: none !important;

    &:hover {
      color: ${colors.RC_ACTION} !important;
      text-decoration: none !important;
      border-bottom: 1px solid #11A9CE !important;
      padding-bottom: 3px !important;
    }
  }

  title {
    display: block;
    margin: 15px 0;
    font-size: 18px !important;
    font-weight: 500 !important;
    font-size: 16px !important;
     
  }

`;

const TitleContainer = styled.div`
  display: flex;
  height: 60px;
   
  direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
`;

const GeneralContent = styled.div`
  border: 0;
  &::-moz-focus-inner {
    border: 0;
  }
`;

const Content = styled.div`
  direction: ${() => (isRTL() ? 'rtl' : 'ltr')};
`;

const ResponsiveContainer = styled.div`
  ${(parentWidth < 551) && css`
    .content-container-responsive {
      width: 100% !important;
      padding: 0 20px !important;
      box-sizing: border-box !important;
    }
  `};
`;
// End of style

/**
 * This component is for preventing general page details
 *
 * @author Hessan Aminilou
 * @param {object} plugin
 * @param {object} config
 * @param {object} props
 */
const GeneralPage = ({ plugin, config, props }) => {
  const { projectId } = plugin;
  const { selectedLang, districtId, localityId } = config;

  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  const { location } = props;
  const { link } = location.state;

  useEffect(() => {
    if (content && content.title) {
      let { title } = content;
      if (content.title === 'The 7 Steps') title = '3-things';
      const { pageId } = content;
      eventTracker('CMS - General Page', pageId);
    }
  }, [content]);

  useEffect(() => {
    const getPage = async () => {
      try {
        setLoading(true);
        const { data } = await cts_secure('get', apis.getGeneralPage(localityId, link, selectedLang));

        if (
          !data
          && !data.header.success
          && !Object.keys(data.content.response).length
        ) throw new Error('Could not get general page data.');

        const {
          pageDetails: {
            links,
            page_image,
            page_content,
            page_bar_title,
            general_page_id,
          }
        } = data.content.response;

        setContent({
          pageId: general_page_id,
          links,
          data: page_content,
          title: page_bar_title,
          pageImage: page_image
        });

        return undefined;
      } catch (e) {
        setContent({});
        return Promise.reject(e.message);
      } finally {
        setLoading(false);
      }
    };
    getPage();
  }, [districtId, selectedLang, link, projectId, localityId]);

  return (
    <Container>
      {
        loading
          ? (<Loader />)
          : (
            <ResponsiveContainer>
              {/* <CTAofCMS /> */}
              <Container className="content-container-responsive">
                {
                  content
                    && Object.keys(content).length
                    ? (
                      <div>
                        <TitleContainer>
                          <Title>{content.title}</Title>
                        </TitleContainer>
                        <ContentContainer>
                          <Content>
                            {parse(content.data)}
                          </Content>
                          {
                            content.links.length
                              ? (
                                <GeneralContent tabIndex="-1">
                                  {
                                    content.links.map((lnk) => (
                                      <Drawer
                                        key={lnk.drawer_title}
                                        link={lnk}
                                        lang={selectedLang}
                                      />
                                    ))
                                  }
                                </GeneralContent>
                              ) : null
                          }
                        </ContentContainer>
                      </div>
                    ) : null
                }
              </Container>
            </ResponsiveContainer>
          )
      }
    </Container>
  );
};

export default GeneralPage;
